
import { Vue, Component } from 'nuxt-property-decorator'
import MeQuery from '@/graphql/queries/Me.gql'
import ClientPermissionsQuery from '@/graphql/queries/ClientPermissions.gql'
import {
  CustomerClientPermission,
  type CurrentUser,
  ClientPermissionsScope,
} from '~/types/types'
import { type SidebarItemDetails } from '~/types'

@Component({
  apollo: {
    me: {
      query: MeQuery,
    },
    clientPermissions: {
      query: ClientPermissionsQuery,
      variables: {
        scope: {
          scope: ClientPermissionsScope.Dashboard,
        },
      },
      skip() {
        return !this.$isClient(this.me)
      },
    },
  },
})
export default class MenuSideBar extends Vue {
  me: CurrentUser | null = null
  clientPermissions: CustomerClientPermission[] = []

  CustomerClientPermission = CustomerClientPermission

  get shopId(): string | null {
    return this.$route.params.shopId
  }

  hasClientPermission(permission: CustomerClientPermission): boolean {
    return this.clientPermissions?.includes(permission) || false
  }

  get generalSidebarItems(): Array<SidebarItemDetails> | null {
    if (!this.shopId) return null
    return [
      {
        title: this.$t('sidebar.general.overview').toString(),
        to: this.localePath({
          name: 'shopId-overview',
          params: { shopId: this.shopId },
        }),
      },
    ]
  }

  get brochureSidebarItems(): Array<SidebarItemDetails> | null {
    if (!this.shopId) return null
    return [
      {
        title: this.$t('sidebar.brochures.brochure-reads').toString(),
        to: this.localePath({
          name: 'shopId-brochure-reads',
          params: { shopId: this.shopId },
        }),
      },
      {
        title: this.$t('sidebar.brochures.brochure-pages').toString(),
        to: this.localePath({
          name: 'shopId-brochure-pages',
          params: { shopId: this.shopId },
        }),
      },
    ]
  }

  get customerSidebarItems(): Array<SidebarItemDetails> {
    const items: Array<SidebarItemDetails> = []

    if (
      this.$isStatisticsManager(this.me) ||
      this.hasClientPermission(CustomerClientPermission.Statistics)
    ) {
      items.push({
        title: this.$t('sidebar.customers.title').toString(),
        to: '/shops',
      })
    }

    if (
      this.$isSuperAdmin(this.me) ||
      this.$isAdmin(this.me) ||
      this.$isClientManager(this.me)
    ) {
      items.push({
        title: this.$t('sidebar.users.title').toString(),
        to: '/admin-users',
      })
    }

    if (this.$isSuperAdmin(this.me) || this.$isCustomerSupport(this.me)) {
      items.push({
        title: this.$t('sidebar.app-users.title').toString(),
        to: '/users',
      })
    }

    if (
      this.$isSuperAdmin(this.me) ||
      this.$isAdmin(this.me) ||
      this.$isClientManager(this.me)
    ) {
      items.push({
        title: this.$t('sidebar.clients.title').toString(),
        to: '/admin-clients',
      })
    }

    if (this.$isSuperAdmin(this.me) || this.$isAdmin(this.me)) {
      items.push({
        title: this.$t('sidebar.shops.title').toString(),
        to: '/admin-shops',
      })
    }

    if (this.$isSuperAdmin(this.me) || this.$isAdmin(this.me)) {
      items.push({
        title: this.$t('sidebar.categories.title').toString(),
        to: '/admin-categories',
      })
    }

    return items
  }

  get adminBrochureSidebarItems(): Array<SidebarItemDetails> {
    const items: Array<SidebarItemDetails> = []

    if (
      this.$isSuperAdmin(this.me) ||
      this.$isAdmin(this.me) ||
      this.$isTagManager(this.me) ||
      this.$isTagger(this.me) ||
      (this.$isClient(this.me) &&
        this.hasClientPermission(CustomerClientPermission.BrochureCreate))
    ) {
      items.push({
        title: this.$t('sidebar.brochures.title').toString(),
        to: '/admin-brochures',
      })
    }

    if (
      this.$isSuperAdmin(this.me) ||
      this.$isAdmin(this.me) ||
      this.$isCityManager(this.me)
    ) {
      items.push({
        title: this.$t('sidebar.brochure-order.title').toString(),
        to: '/admin-brochure-order',
      })
    }

    if (
      this.$isSuperAdmin(this.me) ||
      this.$isAdmin(this.me) ||
      (this.$isClient(this.me) &&
        this.hasClientPermission(CustomerClientPermission.BrochureCreate))
    ) {
      items.push({
        title: this.$t('sidebar.brochure-merges.title').toString(),
        to: '/merge-requests',
      })
    }

    if (
      this.$isSuperAdmin(this.me) ||
      this.$isAdmin(this.me) ||
      this.$isTagManager(this.me)
    ) {
      items.push({
        title: this.$t('sidebar.brands.title').toString(),
        to: '/admin-brands',
      })
    }

    if (
      this.$isSuperAdmin(this.me) ||
      this.$isAdmin(this.me) ||
      this.$isTagManager(this.me)
    ) {
      items.push({
        title: this.$t('sidebar.offer-categories.title').toString(),
        to: '/admin-offer-categories',
      })
    }

    if (this.$isSuperAdmin(this.me) || this.$isAdmin(this.me)) {
      items.push({
        title: this.$t('sidebar.cities.title').toString(),
        to: '/admin-cities',
      })
    }

    return items
  }

  get websiteSidebarItems(): Array<SidebarItemDetails> {
    const items: Array<SidebarItemDetails> = []

    if (this.$isSuperAdmin(this.me) || this.$isAdmin(this.me)) {
      items.push({
        title: this.$t('sidebar.website-pages.title').toString(),
        to: '/admin-website-pages',
      })
    }

    if (this.$isSuperAdmin(this.me) || this.$isAdmin(this.me)) {
      items.push({
        title: this.$t('sidebar.website-posts.title').toString(),
        to: '/admin-website-posts',
      })
    }

    if (this.$isSuperAdmin(this.me) || this.$isAdManager(this.me)) {
      items.push({
        title: this.$t('sidebar.ad-campaigns.title').toString(),
        to: '/admin-ad-campaigns',
      })
    }

    if (this.$isSuperAdmin(this.me) || this.$isAdmin(this.me)) {
      items.push({
        title: this.$t('sidebar.shop-card-templates.title').toString(),
        to: '/admin-shop-card-templates',
      })
      items.push({
        title: this.$t('sidebar.generic-products.title').toString(),
        to: '/admin-generic-product-categories',
      })
    }

    if (this.$isSuperAdmin(this.me) || this.$isAdmin(this.me)) {
      items.push({
        title: this.$t('sidebar.app-languages.title').toString(),
        to: '/admin-languages',
      })
    }

    if (this.$isSuperAdmin(this.me)) {
      items.push({
        title: this.$t('sidebar.configuration').toString(),
        to: '/config',
      })
    }

    return items
  }

  get statisticsSidebarItems(): Array<SidebarItemDetails> {
    const items: Array<SidebarItemDetails> = []

    if (this.$isStatisticsManager(this.me)) {
      items.push({
        title: this.$t('sidebar.admin-statistics.title').toString(),
        to: '/admin-statistics',
      })
    }

    if (
      this.$isSuperAdmin(this.me) ||
      this.$isTagManager(this.me) ||
      this.$isTagger(this.me)
    ) {
      items.push({
        title: this.$t('sidebar.tagging-statistics.title').toString(),
        to: '/admin-tagging-statistics',
      })
    }

    return items
  }
}
